import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { formatDateForInput } from "@/shared/lib/date-time";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";

export function ContractsFilter() {
  const nav = useNavigate({ from: "/app/contracts" });
  const search = useSearch({ from: "/app/contracts" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const titleFilter = findCondition(filters, "title");
  const counterpartyFilter = findCondition(filters, "counterparty");
  
  // Find expired filter (endDate < today)
  const expiredFilter = findCondition(filters, "endDate");

  const [titleValue, setTitleValue] = useState<string>(
    (titleFilter?.value as string) || "",
  );
  const [counterpartyValue, setCounterpartyValue] = useState<string>(
    (counterpartyFilter?.value as string) || "",
  );
  const [showExpired, setShowExpired] = useState<boolean>(
    expiredFilter?.op === "lt" || expiredFilter?.op === "lte",
  );

  useEffect(
    () => setTitleValue((titleFilter?.value as string) || ""),
    [titleFilter?.value],
  );
  useEffect(
    () => setCounterpartyValue((counterpartyFilter?.value as string) || ""),
    [counterpartyFilter?.value],
  );
  useEffect(() => {
    setShowExpired(expiredFilter?.op === "lt" || expiredFilter?.op === "lte");
  }, [expiredFilter?.op]);

  const debouncedTitle = useDebounceCallback((val: string) => {
    setTitleValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "title",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "title");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const debouncedCounterparty = useDebounceCallback((val: string) => {
    setCounterpartyValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "counterparty",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "counterparty");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const handleExpiredFilter = (checked: boolean) => {
    setShowExpired(checked);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    
    if (checked) {
      const today = formatDateForInput(new Date());
      nextFilters = upsertCondition(filters, {
        field: "endDate",
        op: "lt",
        value: `${today} 00:00:00`,
      });
    } else {
      nextFilters = removeConditions(filters, "endDate");
    }
    
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const hasFilter = !!(
    titleFilter?.value ||
    counterpartyFilter?.value ||
    showExpired
  );

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="contracts-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຕາມຊື່ສັນຍາ..."
            defaultValue={titleValue}
            onChange={(e) => debouncedTitle(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <Input
            placeholder="ຄົ້ນຫາຕາມຄູ່ສັນຍາ..."
            defaultValue={counterpartyValue}
            onChange={(e) => debouncedCounterparty(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <Button
            variant={showExpired ? "default" : "outline"}
            size="sm"
            onClick={() => handleExpiredFilter(!showExpired)}
            className="h-8 whitespace-nowrap"
          >
            ສັນຍາຫມົດອາຍຸ
          </Button>
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
